class QuickFilterNav extends HTMLElement {
  constructor() {
    super();
    this.sourceContainer = document.querySelector('#FacetFiltersContainer');
    this.observer = null;
    this.renderFrame = null;
    
    this.keys = {
      gender: 'filter.p.m.custom.gender',
      age: 'filter.v.option.size',
      size: 'filter.v.m.custom.mk_size',
      category: 'filter.p.m.custom.category'
    };
    
    this.MIN_ITEM_COUNT = 10;
    this.RESCUE_MIN_COUNT = 4; 
    this.TARGET_TOTAL_BUTTONS = 10;

    // Master Sort Dictionary based on provided visual constraints
    this.MASTER_ORDER = [
      // Gender
      "BOYS", "GIRLS", "UNISEX", "WOMEN",
      // Age Group
      "Under 1 Year", "1+ years", "2+ years", "4+ years",
      // Size
      "NB", "0-3M", "3-6M", "0-6M", "6-9M", "9-12M", "6-12M", "12-18M", "18-24M",
      "1-2Y", "2-3Y", "2-4Y", "3-4Y", "3-8Y", "4-5Y", "4-6Y", "5-6Y", "6-7Y", "6-8Y", "6M+", "7-8Y",
      // Shared fallbacks
      "ONESIZE", "Other Sizes"
    ];
  }

  connectedCallback() {
    if (!this.sourceContainer) return;
    if (!this.querySelector('.quick-filter-wrapper')) this.render();
    this.initObserver();
    
    this.addEventListener('click', (e) => {
      const button = e.target.closest('button[data-filter-name][data-filter-value]');
      if (button) this.proxyClick(button.dataset.filterName, button.dataset.filterValue);
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.renderFrame !== null) {
      cancelAnimationFrame(this.renderFrame);
      this.renderFrame = null;
    }
  }

  initObserver() {
    this.observer = new MutationObserver(() => this.scheduleRender());
    this.observer.observe(this.sourceContainer, { childList: true, subtree: true });
  }

  scheduleRender() {
    if (this.renderFrame !== null) return;
    this.renderFrame = requestAnimationFrame(() => {
      this.renderFrame = null;
      this.render();
    });
  }

  extractData() {
    const data = { gender: [], age: [], size: [], category: [] };
    const labelsByFor = new Map(
      Array.from(this.sourceContainer.querySelectorAll('label[for]')).map((label) => [label.htmlFor, label])
    );
    
    Object.entries(this.keys).forEach(([key, nameAttr]) => {
      const inputs = this.sourceContainer.querySelectorAll(`input[name="${nameAttr}"]`);
      
      inputs.forEach(input => {
        const label = labelsByFor.get(input.id);
        if (!label) return;
        
        const countEl = label.querySelector('.count');
        const count = countEl ? parseInt(countEl.textContent.trim(), 10) : 0;
        
        const textNode = label.querySelector('.reversed-link__text');
        const text = textNode ? textNode.textContent.trim().split('\n')[0] : '';
        
        data[key].push({
          id: input.id,
          name: input.name,
          value: input.value,
          text: text,
          count: count,
          checked: input.checked
        });
      });
      
      // Initial weight sort for the backfill logic
      data[key].sort((a, b) => b.count - a.count);
    });
    
    return data;
  }

  computeLayout(data) {
    let displayList = [];
    const activeFilters = []; 
    
    const getValidItems = (items) => items.filter(item => item.checked || item.count >= this.MIN_ITEM_COUNT);

    const validGender = getValidItems(data.gender);
    const validAge = getValidItems(data.age);
    const validSize = getValidItems(data.size);
    const validCategory = getValidItems(data.category);

    const isEligible = (validItems) => validItems.length > 1;

    const hasGender = isEligible(validGender);
    const hasAge = isEligible(validAge);
    const hasSize = isEligible(validSize);
    const hasCategory = isEligible(validCategory);

    const addItems = (items, limit = null) => {
      let added = 0;
      for (const item of items) {
        if (limit !== null && added >= limit) break;
        if (!displayList.some(i => i.id === item.id)) {
          displayList.push({ ...item, displayLabel: item.text });
          added++;
        }
      }
    };

    // 1. Extract actively selected items
    Object.values(data).flat().forEach(item => {
      if (item.checked) activeFilters.push({ ...item, displayLabel: item.text });
    });

    // 2. Cascade Routing
    if (hasGender) {
      addItems(validGender);
      if (hasAge) addItems(validAge, 2);
    } else if (hasAge) {
      addItems(validAge);
      if (hasSize) addItems(validSize, 4);
    } else if (hasCategory) {
      addItems(validCategory, 4);
    }

    // 3. Global Constraint: Always append Size if eligible
    if (hasSize) {
      addItems(validSize, 4); 
    }

    // 4. Primary Backfill 
    if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
      addItems(validSize, this.TARGET_TOTAL_BUTTONS - displayList.length);
    }
    if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
      addItems(validCategory, this.TARGET_TOTAL_BUTTONS - displayList.length);
    }

    // 5. Secondary Rescue Backfill
    if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
      const getRescueItems = (items) => items.filter(item => !item.checked && item.count >= this.RESCUE_MIN_COUNT && item.count < this.MIN_ITEM_COUNT);

      addItems(getRescueItems(data.size), this.TARGET_TOTAL_BUTTONS - displayList.length);
      
      if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
        addItems(getRescueItems(data.category), this.TARGET_TOTAL_BUTTONS - displayList.length);
      }
      if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
        addItems(getRescueItems(data.age), this.TARGET_TOTAL_BUTTONS - displayList.length);
      }
      if (displayList.length < this.TARGET_TOTAL_BUTTONS) {
        addItems(getRescueItems(data.gender), this.TARGET_TOTAL_BUTTONS - displayList.length);
      }
    }

    // 6. Inject Actives
    activeFilters.forEach(active => {
      if (!displayList.some(i => i.id === active.id)) {
        displayList.push(active);
      }
    });

    // 7. FINAL SORTING PASS: Align output with Master Dictionary
    displayList.sort((a, b) => {
      let indexA = this.MASTER_ORDER.indexOf(a.text);
      let indexB = this.MASTER_ORDER.indexOf(b.text);
      
      // If item is not in dictionary, push to end
      if (indexA === -1) indexA = 9999;
      if (indexB === -1) indexB = 9999;
      
      // If both are unknown (e.g. two unlisted categories), keep their logic-generated order
      if (indexA === 9999 && indexB === 9999) return 0;
      
      return indexA - indexB;
    });

    return displayList;
  }

  render() {
    const extractedData = this.extractData();
    const layout = this.computeLayout(extractedData);

    if (layout.length === 0) {
      this.innerHTML = '';
      return;
    }

    const html = layout.map(item => `
      <button 
        type="button" 
        data-filter-name="${this.escapeAttribute(item.name)}"
        data-filter-value="${this.escapeAttribute(item.value)}"
        class="quick-filter-pill ${item.checked ? 'is-active' : ''}"
      >
        ${item.displayLabel} <span class="pill-count">(${item.count})</span>
      </button>
    `).join('');

    this.innerHTML = `
      <div class="quick-filter-wrapper flex flex-wrap gap-2 mb-6">
        ${html}
      </div>
    `;
  }

  escapeAttribute(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  proxyClick(filterName, filterValue) {
    const targetInput = Array.from(this.sourceContainer.querySelectorAll('input')).find(
      (input) => input.name === filterName && input.value === filterValue
    );
    if (!targetInput) return;
    
    targetInput.checked = !targetInput.checked;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('quick-filter-nav', QuickFilterNav);